import { Component, Event, EventEmitter, h, Host, Prop } from "@stencil/core";
import api from "../../utils/api";
import { ButtonLabelVisibility } from "../../utils/buttonLabelVisibility";

/** @deprecated use async-toggle-button instead */
@Component({
    tag: "ux-toggle-button",
    styleUrl: "toggle-button.scss",
    shadow: false
})
export class ToggleButton {
    /** is the toggle on or off */
    @Prop({ mutable: true, reflect: true }) on = false;
    /** this id is used to identify similar toggleButtons to synchronise them */
    @Prop() readonly syncId: string = "";
    /** optional url to be called when the item should be toggled */
    @Prop() readonly toggleUrl: string = "";
    /** optional url to be called when the item should be untoggled (-> off), can be left empty if it is identical to toggleUrl */
    @Prop() readonly untoggleUrl: string = "";
    /** text to show on toggle button in "on" state (if used with icon it will be the title attribute) */
    @Prop() readonly textOn: string = "";
    /** text to show on toggle button in "off" state (if used with icon it will be the title attribute) */
    @Prop() readonly textOff: string = "";
    /** name of icon to show when in state "on" */
    @Prop() readonly iconOn: string = "";
    /** name of icon to show when in state "off" */
    @Prop() readonly iconOff: string = "";
    /** name of label to show when in state "on" */
    @Prop() readonly labelOn: string = "";
    /** name of label to show when in state "off" */
    @Prop() readonly labelOff: string = "";
    /** the component sets this to true when an error occurs while communicating with the API */
    @Prop({ mutable: true, reflect: true }) error = false;
    /** Breakpoint when label should be visible */
    @Prop() readonly labelVisibility: ButtonLabelVisibility = ButtonLabelVisibility.Default;

    /** when button is clicked - returns the new toggle state as boolean */
    @Event({
        eventName: "uxToggle",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    didToggle: EventEmitter<boolean>;
    /** when call to toggle url failed - returns the original toggle state as boolean */
    @Event({
        eventName: "uxToggleFailed",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    toggleFailed: EventEmitter<boolean>;

    private toggle(): void {
        const { on } = this;
        this.on = !on;
        this.error = false;
        this.waitForToggle(
            () => {
                this.didToggle.emit(this.on);
                this.syncOtherButtons();
            },
            () => {
                this.on = on;
                this.error = true;
                this.toggleFailed.emit(this.on);
            }
        );
    }

    private syncOtherButtons() {
        const { syncId, on } = this;
        if (syncId === "") {
            return;
        }
        const sameButtons = document.querySelectorAll(`ux-toggle-button[sync-id=${syncId}]`);
        [].forEach.call(sameButtons, (button) => {
            button.on = on;
        });
    }

    private waitForToggle(callback: () => void, error: () => void) {
        const { toggleUrl, untoggleUrl, on } = this;
        if (toggleUrl === "") {
            callback();
            return;
        }
        const url = on && untoggleUrl !== "" ? untoggleUrl : toggleUrl;
        api(url).then(callback).catch(error);
    }

    render() {
        const baseClass = "ux-toggle-button";
        const { on, textOff, textOn, iconOff, iconOn, error, labelOn, labelOff } = this;
        const text = on ? textOn : textOff;
        const hasIcons = iconOn !== "" && iconOff !== "";
        return (
            <Host
                class={{
                    [baseClass]: true,
                    "ux-toggle-button--on": on,
                    "ux-toggle-button--off": !on,
                    "ux-toggle-button--has-error": error,
                    [`${baseClass}__label-visibility ${baseClass}__label-visibility--${this.labelVisibility}`]:
                        this.labelVisibility !== "default"
                }}
                onClick={(e: UIEvent) => {
                    e.preventDefault();
                    this.toggle();
                }}
                title={hasIcons ? text : ""}
            >
                {labelOn !== "" && <span class="ux-toggle-button__label ux-toggle-button__label--on">{labelOn}</span>}
                {labelOff !== "" && <span class="ux-toggle-button__label ux-toggle-button__label--off">{labelOff}</span>}
                {!hasIcons && text}
                {hasIcons && <ux-icon name={on ? iconOn : iconOff} weight="normal" />}
                <span style={{ display: "none" }}>
                    <slot />
                </span>
            </Host>
        );
    }
}
